import { BankAccountPurpose, BillStatus, IAccountConnectionStatusResponse, ICreditReport, IRating } from 'realhaus-sdk';
import { firebaseConfig } from '../firebase.config';
import { User } from 'firebase/auth';

interface IApiRequestParameters<T> {
  token: string;
  body: T;
};

interface IAddTenantLeasePayload {
  leaseId: string;
}

interface IBankAccountPurposeRequest {
  bankAccountPurpose: BankAccountPurpose;
}

interface IGetBankAccountsByIdRequest {
  id: string;
}

export const addTenantFromInviteToLease = async (request: IApiRequestParameters<IAddTenantLeasePayload>) => {
  const { token, body } = request;
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/lease/addTenantToLease`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
        body: JSON.stringify({ ...body })
      }
    );
    return response.status === 200;
  } catch (ex) {
    console.error('An error occurred while adding tenant to lease agreement!');
  }
  return false;
};

export interface IBillPaymentRequest extends IPaymentRequest {
  leaseId: string,
  billId: string
}

export interface ICreditReportSeatRequest extends IPaymentRequest {
  noOfSeats: number;
}
export interface IPaymentRequest {
  type: 'BILL' | 'CREDIT_REPORT_SEAT';
  amount: number;
  trxFees: number;
  method: string;
  vendorPayload?: IVendorPayload
}

export interface IVendorPayload {
  finix?: { token?: string, sessionId?: string }
  stripe?: {
    returnUrl?: string;
    successUrl?: string;
  }
}

export interface IBillPayResponse {
  errorMsg?: string;
  trxRefId?: string;
  interacUrl?: string;
  trxLogId?: string;
  paramountInteracPayload?: {
    details: string;
    merchantId: string;
    iv: string
    paymentUrl: string;
  };
  stripe?: {
    checkoutSessionId: string;
    checkoutSessionClientSecret: string;
  }
}

export const initiatePayment = async (req: IApiRequestParameters<IBillPaymentRequest | ICreditReportSeatRequest>): Promise<IBillPayResponse | undefined> => {
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/wallet/payment`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${req.token}`,
      },
      method: 'POST',
      body: JSON.stringify({ ...req.body })
    });

    if (response.ok) {
      return (await response.json()) as IBillPayResponse;
    } else {
      console.error(`Paybill Response ${response.status}`, { responseStatus: response.status, responseText: response.statusText });
      console.error(response.json());
      console.error(response.statusText);
      if (response.status === 409) {
        return { errorMsg: response.statusText };
      }
    }
  } catch (err) {
    console.error(err);
    throw new Error("Failed to initiate payment ");
  }
  finally { }
}


export const cancelStripeCheckoutSession = async (req: IApiRequestParameters<{ checkoutSessionId: string }>): Promise<void> => {
  try {
    await fetch(`${firebaseConfig.cloudFunctionApiURL}/stripe/cancel-checkout-session`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${req.token}`,
      },
      method: 'POST',
      body: JSON.stringify({ ...req.body }),
    });
  } catch (err) {
    console.error('Failed to cancel Stripe checkout session', err);
  }
};

export const resendWithdrawalInstruction = async (req: IApiRequestParameters<{ trxLogId: string }>): Promise<boolean | undefined> => {
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/wallet/sendWithdrawInstructions`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${req.token}`,
      },
      method: 'POST',
      body: JSON.stringify({ ...req.body })
    });
    return response.status === 200
  } catch (err) {
    console.log(err);
    console.error('An error occured while trying to resend withdrawal instructions');
    throw new Error('Failed to send withdraw instructions');
  }
  finally { }
}

interface ISendMoney {
  leaseId: string;
  amount: number;
  receiverId: string;
  notes: string;
}

// Creates a bill on the lease owed by the sender to the receiver; the sender then pays it through
// the usual checkout. Returns the new bill id, or the server's reason it was refused.
export const sendMoney = async (
  request: IApiRequestParameters<ISendMoney>,
): Promise<{ billId?: string; error?: string }> => {
  const { token, body } = request;
  const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/lease/sendMoney`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    method: 'POST',
    body: JSON.stringify({ ...body }),
  })
  if (response.status === 200) {
    return (await response.json()) as { billId: string };
  }
  return { error: (await response.text()) || 'Unable to send money' };
}

interface IRequestPayment {
  amount: number;
  leaseId: string;
  debtorId: string;
  dueDate: number;
  reason: string;
  attachments?: string[]
}

export const requestPayment = async (req: IApiRequestParameters<IRequestPayment>) => {
  const { token, body } = req;

  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/lease/requestPayment`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      body: JSON.stringify({ ...body })
    })

    return response.status === 200
  }
  catch (err) {
    console.error(err);
    console.error('An error occured while trying to request for payment')
  }

}

// Landlord marks a pending bill paid (received outside Realhaus) or cancelled. Bills are
// server-owned, so this goes through the API instead of a Firestore write.
export const updateBillStatus = async (
  req: IApiRequestParameters<{ leaseId: string; billId: string; status: BillStatus }>,
): Promise<{ ok: boolean; message: string }> => {
  const { token, body } = req;

  const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/lease/update-bill-status`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    method: 'POST',
    body: JSON.stringify({ ...body })
  })

  return { ok: response.status === 200, message: await response.text() }
}

export const generateCreditReport = async (req: IApiRequestParameters<{ rentApplicationId: string }>) => {
  const { token, body } = req;

  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/cr/generateCreditReport`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      body: JSON.stringify({ ...body })
    })

    return response.status === 200
  }
  catch (err) {
    console.error(err);
    console.error('An error occured while trying to decline ID Verification');
    return;
  }
}

export const sendEmailVerification = async (req: IApiRequestParameters<{ user: User }>) => {
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionPublicApiUrl}/user/send-email-verification`, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ ...req.body })
    });

    if (response.ok) {
      return response.status === 200;
    } else {
      console.error(`Failed to resend email verification`, { responseStatus: response.status, responseText: response.statusText });
      console.error(response.json());
      console.error(response.statusText);
      if (response.status === 409) {
        return { errorMsg: response.statusText };
      }
    }
  } catch (err) {
    console.error(err);
    throw new Error("Failed to resend email verification ");
  }
  finally { }
}

export const sendWelcomeEmail = async (req: IApiRequestParameters<{ email: string }>) => {
  const { token, body } = req;
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionPublicApiUrl}/user/send-welcome-email`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      body: JSON.stringify({ ...body })
    })

    if (response.ok) {
      return response.status === 200;
    } else {
      console.error(`Failed to send welcome email`, { responseStatus: response.status, responseText: response.statusText });
      console.error(response.json());
      if (response.status === 409) {
        return { errorMsg: response.statusText };
      }
    }
  }
  catch (err) {
    console.error(err);
    console.error('An error occured while trying to send welcome email');
    return;
  }
}

export const createStripeConnectedAccount = async (req: IApiRequestParameters<{}>): Promise<string | undefined> => {
  const { token } = req;
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/stripe/create-connect-account`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
    })

    if (response.ok) {
      const data = await response.json();
      return data.accountId as string;
    } else {
      console.error(`Failed to create stripe connected account`, { responseStatus: response.status, responseText: response.statusText });
      console.error(await response.json());
    }
  } catch (err) {
    console.error(err);
    console.error('An error occured while trying to create stripe connected account');
    return;
  }
}

export const createStripeAccountSession = async (req: IApiRequestParameters<{ accountId: string }>): Promise<string | undefined> => {
  const { token, body } = req;
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/stripe/create-account-session`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      body: JSON.stringify({ ...body })
    })

    if (response.ok) {
      const data = await response.json();
      return data.client_secret as string;
    } else {
      console.error(`Failed to create stripe account session`, { responseStatus: response.status, responseText: response.statusText });
      console.error(await response.json());
      throw new Error('Failed to create stripe account session');
    }
  } catch (err) {
    console.error('Failed to create stripe account session');
    console.error(err);
    return;
  }
}

export interface IListingLeaseSummary {
  isLeased: boolean;
  ratings: IRating[];
}

/**
 * Whether a listing is currently leased, and its lease ratings. Computed by the server because
 * lease documents are readable only by their landlord and tenants.
 */
export const getListingLeaseSummary = async (req: IApiRequestParameters<{ listingId: string }>): Promise<IListingLeaseSummary | undefined> => {
  const { token, body } = req;
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/listing/${encodeURIComponent(body.listingId)}/lease-summary`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    });

    if (response.ok) {
      return (await response.json()) as IListingLeaseSummary;
    }
    console.error('Failed to retrieve listing lease summary', { responseStatus: response.status });
  } catch (err) {
    console.error('Failed to retrieve listing lease summary');
    console.error(err);
  }
  return undefined;
};

/**
 * The credit report of a rent application's applicant, for the landlord of its listing. Credit
 * reports are readable only by the person they are about, so landlords get them from the server.
 * Resolves to null when the applicant has no report, and undefined when the request fails.
 */
export const getApplicantCreditReport = async (req: IApiRequestParameters<{ rentApplicationId: string }>): Promise<ICreditReport | null | undefined> => {
  const { token, body } = req;
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/rent-application/${encodeURIComponent(body.rentApplicationId)}/credit-report`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    });

    if (response.ok) {
      return ((await response.json()) as { creditReport: ICreditReport | null }).creditReport;
    }
    console.error('Failed to retrieve credit report', { responseStatus: response.status });
  } catch (err) {
    console.error('Failed to retrieve credit report');
    console.error(err);
  }
  return undefined;
};

export const getStripeConnectAccountId = async (req: IApiRequestParameters<{}>): Promise<string | undefined> => {
  const { token } = req;
  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/bank/connect-account-id`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    });

    if (response.ok) {
      const data = await response.json();
      return data.accountId as string;
    } else if (response.status === 404) {
      return undefined;
    } else {
      console.error('Failed to retrieve connect account ID', { responseStatus: response.status });
      return undefined;
    }
  } catch (err) {
    console.error('Failed to retrieve connect account ID');
    console.error(err);
    return undefined;
  }
};

export const getBankAccountSetupStatus = async (req: IApiRequestParameters<{ userId: string }>) => {
  const { token, body } = req;

  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/bank/account-connection-status/${body.userId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    })

    if (response.ok) {
      const data = await response.json();
      return data as IAccountConnectionStatusResponse;
    } else if (response.status >= 400 && response.status < 500) {
      console.error(`Failed to retrieve account connection status`, { responseStatus: response.status, responseText: await response.text() });
    }
    return;
  } catch (err) {
    console.error('Failed to retrieve account connection status');
    console.error(err);
    return;
  }
};

type CreatePADSetupResponse = {
  client_secret: string;
  intent_id?: string;
  session_id?: string;
}
export type CreatePADSetupResult =
  | { kind: 'ok'; clientSecret: string; }
  | { kind: 'already-complete' }
  | { kind: 'error' };

export const createPADSetup = async (req: IApiRequestParameters<{ leaseId: string }>): Promise<CreatePADSetupResult> => {
  const { token, body } = req;

  try {
    const response = await fetch(`${firebaseConfig.cloudFunctionApiURL}/stripe/create-pad-setup`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      body: JSON.stringify({ ...body })
    });

    if (response.ok) {
      const data = await response.json() as CreatePADSetupResponse;
      return {
        kind: 'ok',
        clientSecret: data.client_secret
      };
    }

    if (response.status === 409) {
      return { kind: 'already-complete' };
    }

    console.error(`Failed to create PAD setup`, { responseStatus: response.status, responseText: response.statusText });
    return { kind: 'error' };
  } catch (err) {
    console.error('Failed to create PAD setup');
    console.error(err);
    return { kind: 'error' };
  }
}